import { Injectable, signal, WritableSignal } from '@angular/core';
import { logEvent } from '../app.config';
import { isLabelPreviewBackgroundId, LabelPreviewBackgroundId } from '../common/label-preview-backgrounds';
import { EcoUpdateBehavior } from '../models/economy-models';
import { LOCAL_STORAGE_KEY_REVISIT } from '../const';
import { poe1, poe2 } from '../../game';

const prefix = poe1 ? 'poe1' : 'poe';

const KEY_HIDE_CONTINUE_WARNING = `${prefix}-hide-continue-warning`;
const KEY_DISMISS_TUTORIALS = `${prefix}-dismiss-tutorials`;
const KEY_TUTORIAL_COMPLETE = `${prefix}-hide-tutorial-complete`;
const KEY_AUTO_VIEW_CHANGELOG = `${prefix}-auto-view-changelog`;
const KEY_AUTO_PREVIEW_PRESET_CHANGES = `${prefix}-auto-preview-preset-changes`;
const KEY_HIGHLIGHT_STATS = `${prefix}-highlight-stats`;
const KEY_INCLUDE_CRIT_IN_WEAPON_DPS = `${prefix}-include-crit-in-weapon-dps`;
const KEY_KEEP_ITEM_LABEL_TOOLTIPS_OPEN_ON_HOVER = `${prefix}-keep-item-label-tooltips-open-on-hover`;
const KEY_SHOW_VALUE_IN_DROPDOWNS = `${prefix}-show-value-in-dropdowns`;
const KEY_BG_IMAGE = `${prefix}-bg-image-040`;
const KEY_HIDE_TIER_LISTS_MESSAGE = `${prefix}-hide-tier-lists-message`;
const KEY_HIDE_UNIQUES_TIER_LIST_MESSAGE = `${prefix}-hide-uniques-tier-list-message`;
const KEY_HIDE_MODS_TIER_LIST_MESSAGE = `${prefix}-hide-mods-tier-list-message`;
const KEY_HIDE_GEAR_TIER_LIST_MESSAGE = `${prefix}-hide-gear-tier-list-message`;
const KEY_HIDE_JEWELLERY_TIER_LIST_MESSAGE = `${prefix}-hide-jewellery-tier-list-message`;
const KEY_HIDE_ITEMS_TIER_LIST_MESSAGE = `${prefix}-hide-items-tier-list-message`;
const KEY_HIDE_ITEM_LIBRARY_EXPLAINER = `${prefix}-hide-item-library-explainer`;
const KEY_SHOW_DROP_INFO = `${prefix}-show-drop-info`;
const KEY_HIDE_MOVED_CURRENCY_UNIQUES = `${prefix}-hide-moved-currency-uniques`;
const KEY_MOVED_TIERS_GROUPING_MODE = `${prefix}-moved-tiers-grouping-mode`;
const KEY_HIDE_ICONS_IN_DROPDOWNS = `${prefix}-hide-icons-in-dropdowns`;
const KEY_SHOW_UNIQUES_DUST_VALUE_PER_SLOT = `${prefix}-show-uniques-dust-value-per-slot`;
const KEY_HIDE_UPCOMING_PATCH_WARNING = poe2 ? `${prefix}-hide-upcoming-patch-050-warning` : `${prefix}-hide-upcoming-patch-329-warning`;
const KEY_HIDE_UPCOMING_PATCH_TOP_BAR = poe2 ? `${prefix}-hide-upcoming-patch-050-top-bar-1` : `${prefix}-hide-upcoming-patch-329-top-bar`;
const KEY_STICKY_NAVIGATION = `${prefix}-sticky-navigation`;
const KEY_FLOATING_CONTROLS = `${prefix}-floating-controls`;
const KEY_ADD_MORE_COLOR_PRESETS = `${prefix}-add-more-color-presets`;
const KEY_ENABLE_RULE_NAVIGATION = `${prefix}-enable-rule-navigation`;
const KEY_ALWAYS_GROUP_BY_BASE_TYPE = `${prefix}-always-group-by-base-type`;
const KEY_SHOW_ALL_OTHER_UNIQUES = `${prefix}-show-all-other-uniques`;
const KEY_ECO_UPDATE_BEHAVIOR_LAUNCH = `${prefix}-eco-update-behavior-launch`;
const KEY_ECO_UPDATE_BEHAVIOR_LAUNCH_SHOW_UPDATES = `${prefix}-eco-update-behavior-launch-show-updates`;
const KEY_ECO_UPDATE_BEHAVIOR_UPDATE = `${prefix}-eco-update-behavior-update`;
const KEY_ECO_UPDATE_BEHAVIOR_UPDATE_ENABLE = `${prefix}-eco-update-behavior-update-enable`;
const KEY_ECO_UPDATE_BEHAVIOR_UPDATE_BLINK = `${prefix}-eco-update-behavior-update-blink`;
const KEY_SHOW_EXTRA_TAB = `${prefix}-show-extra-tab`;
const KEY_ENABLE_CUSTOM_RULE_GROUPS = `${prefix}-enable-custom-rule-groups`;
const KEY_ENABLE_CUSTOM_RULE_LABELS = `${prefix}-enable-custom-rule-labels`;
const KEY_QUICK_APPLY_STYLE_LIBRARY_IN_ITEM_TIER_PICKER = `${prefix}-quick-apply-style-library-in-item-tier-picker`;
const KEY_SKIP_REMOVE_CONFIRM_DIALOGS = `${prefix}-skip-remove-confirm-dialogs`;
const KEY_AUTOCOMPLETE_RETAIN_SEARCH = `${prefix}-autocomplete-retain-search`;
const KEY_LABEL_GLOW = `${prefix}-label-glow`;
const KEY_LABEL_PREVIEW_BG = `${prefix}-label-preview-bg`;
const KEY_NUMERIC_ALERT_SOUND_VOLUME = `${prefix}-numeric-alert-sound-volume`;
const KEY_LIMIT_GEAR_TIER_LIST_TO_MY_GEAR = `${prefix}-limit-gear-tier-list-to-my-gear`;
const KEY_QUICK_FILTERS_AS_MAIN_TAB = `${prefix}-quick-filters-as-main-tab`;
const KEY_HIDE_QUICK_FILTERS_MAIN_TAB_BANNER = `${prefix}-hide-quick-filters-main-tab-banner`;
const KEY_HIDE_PASTE_RULE_EXPLAINERS = `${prefix}-hide-paste-rule-explainers`;
const KEY_HIDE_PASTE_UNIQUE_EXPLAINERS = `${prefix}-hide-paste-unique-explainers`;
const KEY_HIDE_PASTE_EXPLAINERS = `${prefix}-hide-paste-explainers`;
const KEY_FILTER_SNAPSHOT_STORAGE = `${prefix}-filter-snapshot-storage`;
const KEY_FILTER_SNAPSHOT_USERNAME = `${prefix}-filter-snapshot-username`;
const KEY_FILTER_SHARE_LINK_USERNAME = `${prefix}-filter-share-link-username`;
const KEY_HIDE_FILTER_SNAPSHOT_CONSENT_BANNER = `${prefix}-hide-filter-snapshot-consent-banner`;
const KEY_SETTINGS_GROUP_VISIBILITY = `${prefix}-settings-group-visibility`;

const DEFAULT_BG_IMAGE = poe1 ? 'sovereign' : 'aldur-rune2';

export type SettingsGroupId =
  | 'general'
  | 'privacySharing'
  | 'display'
  | 'navigationLayout'
  | 'economyUpdates'
  | 'advancedFeatures'
  | 'labelPreviewBackground';

export type SettingsGroupVisibility = Record<SettingsGroupId, boolean>;

const DEFAULT_SETTINGS_GROUP_VISIBILITY: SettingsGroupVisibility = {
  general: true,
  privacySharing: true,
  display: true,
  navigationLayout: true,
  economyUpdates: true,
  advancedFeatures: true,
  labelPreviewBackground: true,
};

// For bootstrap error recovery in main.ts before DI is available
export const SETTINGS_KEY_DISMISS_TUTORIALS = KEY_DISMISS_TUTORIALS;

function readStrict(raw: string | null): boolean {
  return raw === '1';
}

function write(key: string, value: boolean): void {
  localStorage.setItem(key, value ? '1' : '0');
}

function readDefaultOn(raw: string | null): boolean {
  return raw !== '0';
}

function trackSetting(key: string, name: string, category = 'settings'): void {
  const action = key.startsWith(`${prefix}-`) ? key.slice(prefix.length + 1) : key;
  logEvent(category, action.replace(/-/g, '_'), name);
}

function readVisible(raw: string | null): boolean {
  return raw !== '1';
}

function hideMessage(key: string): void {
  localStorage.setItem(key, '1');
}

function clearMessage(key: string): void {
  localStorage.removeItem(key);
}

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private static _instance: SettingsService;

  static get instance(): SettingsService {
    return SettingsService._instance;
  }
  readonly autoViewChangelog = signal(false);
  readonly autoPreviewPresetChanges = signal(true);
  readonly highlightStats = signal(false);
  readonly includeCritInWeaponDps = signal(false);
  readonly keepItemLabelTooltipsOpenOnHover = signal(false);
  readonly showValueInDropdowns = signal(false);
  readonly hideMovedCurrencyUniques = signal(false);
  readonly hideIconsInDropdowns = signal(false);
  readonly showUniquesDustValuePerSlot = signal(false);
  readonly stickyNavigation = signal(true);
  readonly floatingControls = signal(true);
  readonly addMoreColorPresets = signal(false);
  readonly enableRuleNavigation = signal(true);
  readonly ecoUpdateBehaviorLaunch = signal(EcoUpdateBehavior.AutoUpdateAndSync);
  readonly ecoUpdateBehaviorLaunchShowUpdates = signal(false);
  readonly ecoUpdateBehaviorUpdate = signal(EcoUpdateBehavior.AutoUpdateAndSync);
  readonly ecoUpdateBehaviorUpdateEnable = signal(true);
  readonly ecoUpdateBehaviorUpdateBlink = signal(true);
  readonly showExtraTab = signal(true);
  readonly enableCustomRuleGroups = signal(false);
  readonly enableCustomRuleLabels = signal(false);
  readonly quickApplyRecentlyCopiedStylesInItemTierPicker = signal(false);
  readonly skipRemoveConfirmDialogs = signal(false);
  readonly autocompleteRetainSearch = signal(false);
  readonly quickFiltersAsMainTab = signal(false);
  readonly filterSnapshotStorageEnabled = signal(true);
  readonly filterSnapshotUsernameEnabled = signal(true);
  readonly filterShareLinkUsernameEnabled = signal(true);

  readonly bgImage = signal(DEFAULT_BG_IMAGE);

  readonly showDropInfo = signal(false);
  readonly alwaysGroupByBaseType = signal(true);
  readonly showAllOtherUniques = signal(false);
  readonly movedTiersGroupingMode = signal<'movement' | 'tier'>('movement');
  readonly showMovedTiers = signal(true);

  readonly showContinueWarning = signal(true);
  readonly showTierListExplainer = signal(true);
  readonly showUniquesTierListMessage = signal(true);
  readonly showModsTierListMessage = signal(true);
  readonly showGearTierListMessage = signal(true);
  readonly showJewelleryTierListMessage = signal(true);
  readonly showItemsTierListMessage = signal(true);
  readonly showItemLibraryExplainer = signal(true);
  readonly showQuickFiltersMainTabBanner = signal(true);
  readonly showFilterSnapshotConsentBanner = signal(true);
  readonly showIncomingPatchWarning = signal(true);
  readonly showIncomingPatchTopBar = signal(true);
  readonly showPasteRuleExplainers = signal(true);
  readonly showPasteUniqueExplainers = signal(true);

  readonly labelGlow = signal<'always' | 'hover' | 'never'>('always');
  readonly labelPreviewBg = signal<LabelPreviewBackgroundId>('bg1');
  readonly numericAlertSoundVolume = signal(false);
  readonly limitGearTierListToMyGear = signal(false);
  readonly settingsGroupVisibility = signal<SettingsGroupVisibility>({ ...DEFAULT_SETTINGS_GROUP_VISIBILITY });

  readonly tutorialsDismissed = signal(false);
  readonly tutorialComplete = signal(false);

  constructor() {
    SettingsService._instance = this;
    this.seedDefaults();
    this.loadAll();
  }

  private seedDefaults(): void {
    if (localStorage.getItem(KEY_SHOW_VALUE_IN_DROPDOWNS) === null) {
      localStorage.setItem(KEY_SHOW_VALUE_IN_DROPDOWNS, '1');
    }
    if (localStorage.getItem(KEY_AUTO_VIEW_CHANGELOG) === null) {
      localStorage.setItem(KEY_AUTO_VIEW_CHANGELOG, '1');
    }
    if (localStorage.getItem(KEY_AUTO_PREVIEW_PRESET_CHANGES) === null) {
      localStorage.setItem(KEY_AUTO_PREVIEW_PRESET_CHANGES, '1');
    }
    if (localStorage.getItem(KEY_ADD_MORE_COLOR_PRESETS) === null) {
      localStorage.setItem(KEY_ADD_MORE_COLOR_PRESETS, '1');
    }
    if (localStorage.getItem(KEY_ENABLE_RULE_NAVIGATION) === null) {
      localStorage.setItem(KEY_ENABLE_RULE_NAVIGATION, '1');
    }
    if (localStorage.getItem(KEY_ECO_UPDATE_BEHAVIOR_LAUNCH) === null) {
      localStorage.setItem(KEY_ECO_UPDATE_BEHAVIOR_LAUNCH, EcoUpdateBehavior.AutoUpdateAndSync);
    }
    if (localStorage.getItem(KEY_ECO_UPDATE_BEHAVIOR_UPDATE) === null) {
      localStorage.setItem(KEY_ECO_UPDATE_BEHAVIOR_UPDATE, EcoUpdateBehavior.AutoUpdateAndSync);
    }
    if (localStorage.getItem(KEY_ECO_UPDATE_BEHAVIOR_UPDATE_ENABLE) === null) {
      localStorage.setItem(KEY_ECO_UPDATE_BEHAVIOR_UPDATE_ENABLE, '1');
    }
    if (localStorage.getItem(KEY_ECO_UPDATE_BEHAVIOR_UPDATE_BLINK) === null) {
      localStorage.setItem(KEY_ECO_UPDATE_BEHAVIOR_UPDATE_BLINK, '1');
    }
    if (localStorage.getItem(KEY_SHOW_EXTRA_TAB) === null) {
      localStorage.setItem(KEY_SHOW_EXTRA_TAB, '1');
    }
    if (localStorage.getItem(KEY_QUICK_FILTERS_AS_MAIN_TAB) === null) {
      const isReturningUser = localStorage.getItem(LOCAL_STORAGE_KEY_REVISIT) === '1';
      localStorage.setItem(KEY_QUICK_FILTERS_AS_MAIN_TAB, isReturningUser ? '1' : '0');
    }
  }

  private applyBoolean(
    setting: WritableSignal<boolean>,
    key: string,
    value: boolean,
    options?: { track?: boolean; afterSet?: (value: boolean) => void },
  ): void {
    setting.set(value);
    write(key, value);
    if (options?.track) {
      trackSetting(key, String(value));
    }
    options?.afterSet?.(value);
  }

  private toggleBoolean(
    setting: WritableSignal<boolean>,
    key: string,
    afterSet?: (value: boolean) => void,
  ): void {
    this.applyBoolean(setting, key, !setting(), { track: true, afterSet });
  }

  private loadAll(): void {
    this.autoViewChangelog.set(readStrict(localStorage.getItem(KEY_AUTO_VIEW_CHANGELOG)));
    this.autoPreviewPresetChanges.set(readDefaultOn(localStorage.getItem(KEY_AUTO_PREVIEW_PRESET_CHANGES)));
    this.highlightStats.set(readStrict(localStorage.getItem(KEY_HIGHLIGHT_STATS)));
    this.includeCritInWeaponDps.set(readStrict(localStorage.getItem(KEY_INCLUDE_CRIT_IN_WEAPON_DPS)));
    this.keepItemLabelTooltipsOpenOnHover.set(readStrict(localStorage.getItem(KEY_KEEP_ITEM_LABEL_TOOLTIPS_OPEN_ON_HOVER)));
    this.showValueInDropdowns.set(readStrict(localStorage.getItem(KEY_SHOW_VALUE_IN_DROPDOWNS)));
    this.hideMovedCurrencyUniques.set(readStrict(localStorage.getItem(KEY_HIDE_MOVED_CURRENCY_UNIQUES)));
    this.hideIconsInDropdowns.set(readStrict(localStorage.getItem(KEY_HIDE_ICONS_IN_DROPDOWNS)));
    this.showUniquesDustValuePerSlot.set(readStrict(localStorage.getItem(KEY_SHOW_UNIQUES_DUST_VALUE_PER_SLOT)));
    this.stickyNavigation.set(readDefaultOn(localStorage.getItem(KEY_STICKY_NAVIGATION)));
    this.floatingControls.set(readDefaultOn(localStorage.getItem(KEY_FLOATING_CONTROLS)));
    this.addMoreColorPresets.set(readStrict(localStorage.getItem(KEY_ADD_MORE_COLOR_PRESETS)));
    this.enableRuleNavigation.set(readDefaultOn(localStorage.getItem(KEY_ENABLE_RULE_NAVIGATION)));
    this.ecoUpdateBehaviorLaunch.set(
      (localStorage.getItem(KEY_ECO_UPDATE_BEHAVIOR_LAUNCH) as EcoUpdateBehavior) || EcoUpdateBehavior.AutoUpdateAndSync
    );
    this.ecoUpdateBehaviorLaunchShowUpdates.set(readStrict(localStorage.getItem(KEY_ECO_UPDATE_BEHAVIOR_LAUNCH_SHOW_UPDATES)));
    this.ecoUpdateBehaviorUpdate.set(
      (localStorage.getItem(KEY_ECO_UPDATE_BEHAVIOR_UPDATE) as EcoUpdateBehavior) || EcoUpdateBehavior.AutoUpdateAndSync
    );
    this.ecoUpdateBehaviorUpdateEnable.set(readDefaultOn(localStorage.getItem(KEY_ECO_UPDATE_BEHAVIOR_UPDATE_ENABLE)));
    this.ecoUpdateBehaviorUpdateBlink.set(readDefaultOn(localStorage.getItem(KEY_ECO_UPDATE_BEHAVIOR_UPDATE_BLINK)));
    this.showExtraTab.set(readDefaultOn(localStorage.getItem(KEY_SHOW_EXTRA_TAB)));
    this.enableCustomRuleGroups.set(readStrict(localStorage.getItem(KEY_ENABLE_CUSTOM_RULE_GROUPS)));
    this.enableCustomRuleLabels.set(readStrict(localStorage.getItem(KEY_ENABLE_CUSTOM_RULE_LABELS)));
    this.quickApplyRecentlyCopiedStylesInItemTierPicker.set(
      readStrict(localStorage.getItem(KEY_QUICK_APPLY_STYLE_LIBRARY_IN_ITEM_TIER_PICKER))
    );
    this.skipRemoveConfirmDialogs.set(readStrict(localStorage.getItem(KEY_SKIP_REMOVE_CONFIRM_DIALOGS)));
    this.autocompleteRetainSearch.set(readStrict(localStorage.getItem(KEY_AUTOCOMPLETE_RETAIN_SEARCH)));
    this.quickFiltersAsMainTab.set(readStrict(localStorage.getItem(KEY_QUICK_FILTERS_AS_MAIN_TAB)));
    this.filterSnapshotStorageEnabled.set(readDefaultOn(localStorage.getItem(KEY_FILTER_SNAPSHOT_STORAGE)));
    this.filterSnapshotUsernameEnabled.set(readDefaultOn(localStorage.getItem(KEY_FILTER_SNAPSHOT_USERNAME)));
    this.filterShareLinkUsernameEnabled.set(readDefaultOn(localStorage.getItem(KEY_FILTER_SHARE_LINK_USERNAME)));

    this.bgImage.set(localStorage.getItem(KEY_BG_IMAGE) ?? DEFAULT_BG_IMAGE);

    this.showDropInfo.set(readStrict(localStorage.getItem(KEY_SHOW_DROP_INFO)));
    this.alwaysGroupByBaseType.set(localStorage.getItem(KEY_ALWAYS_GROUP_BY_BASE_TYPE) !== 'false');
    this.showAllOtherUniques.set(localStorage.getItem(KEY_SHOW_ALL_OTHER_UNIQUES) === 'true');
    this.movedTiersGroupingMode.set(
      (localStorage.getItem(KEY_MOVED_TIERS_GROUPING_MODE) as 'movement' | 'tier') || 'movement'
    );
    this.showMovedTiers.set(readVisible(localStorage.getItem(KEY_HIDE_MOVED_CURRENCY_UNIQUES)));

    this.showContinueWarning.set(readVisible(localStorage.getItem(KEY_HIDE_CONTINUE_WARNING)));
    this.showTierListExplainer.set(readVisible(localStorage.getItem(KEY_HIDE_TIER_LISTS_MESSAGE)));
    this.showUniquesTierListMessage.set(readVisible(localStorage.getItem(KEY_HIDE_UNIQUES_TIER_LIST_MESSAGE)));
    this.showModsTierListMessage.set(readVisible(localStorage.getItem(KEY_HIDE_MODS_TIER_LIST_MESSAGE)));
    this.showGearTierListMessage.set(readVisible(localStorage.getItem(KEY_HIDE_GEAR_TIER_LIST_MESSAGE)));
    this.showJewelleryTierListMessage.set(readVisible(localStorage.getItem(KEY_HIDE_JEWELLERY_TIER_LIST_MESSAGE)));
    this.showItemsTierListMessage.set(readVisible(localStorage.getItem(KEY_HIDE_ITEMS_TIER_LIST_MESSAGE)));
    this.showItemLibraryExplainer.set(readVisible(localStorage.getItem(KEY_HIDE_ITEM_LIBRARY_EXPLAINER)));
    this.showQuickFiltersMainTabBanner.set(readVisible(localStorage.getItem(KEY_HIDE_QUICK_FILTERS_MAIN_TAB_BANNER)));
    this.showFilterSnapshotConsentBanner.set(readVisible(localStorage.getItem(KEY_HIDE_FILTER_SNAPSHOT_CONSENT_BANNER)));
    this.showIncomingPatchWarning.set(readVisible(localStorage.getItem(KEY_HIDE_UPCOMING_PATCH_WARNING)));
    this.showIncomingPatchTopBar.set(readVisible(localStorage.getItem(KEY_HIDE_UPCOMING_PATCH_TOP_BAR)));
    this.showPasteRuleExplainers.set(readVisible(
      localStorage.getItem(KEY_HIDE_PASTE_RULE_EXPLAINERS) ?? localStorage.getItem(KEY_HIDE_PASTE_EXPLAINERS)
    ));
    this.showPasteUniqueExplainers.set(readVisible(localStorage.getItem(KEY_HIDE_PASTE_UNIQUE_EXPLAINERS)));

    this.labelGlow.set((localStorage.getItem(KEY_LABEL_GLOW) as 'always' | 'hover' | 'never') || 'always');
    const labelPreviewBgRaw = localStorage.getItem(KEY_LABEL_PREVIEW_BG);
    const labelPreviewBg: LabelPreviewBackgroundId =
      labelPreviewBgRaw && isLabelPreviewBackgroundId(labelPreviewBgRaw) ? labelPreviewBgRaw : 'bg1';
    this.labelPreviewBg.set(labelPreviewBg);
    this.numericAlertSoundVolume.set(readStrict(localStorage.getItem(KEY_NUMERIC_ALERT_SOUND_VOLUME)));
    this.limitGearTierListToMyGear.set(readStrict(localStorage.getItem(KEY_LIMIT_GEAR_TIER_LIST_TO_MY_GEAR)));
    this.settingsGroupVisibility.set(this.readSettingsGroupVisibility());

    this.tutorialsDismissed.set(readStrict(localStorage.getItem(KEY_DISMISS_TUTORIALS)));
    this.tutorialComplete.set(readStrict(localStorage.getItem(KEY_TUTORIAL_COMPLETE)));
  }

  toggleAutoViewChangelog(): void {
    this.toggleBoolean(this.autoViewChangelog, KEY_AUTO_VIEW_CHANGELOG);
  }

  toggleQuickFiltersAsMainTab(): void {
    this.toggleBoolean(this.quickFiltersAsMainTab, KEY_QUICK_FILTERS_AS_MAIN_TAB);
  }

  setQuickFiltersAsMainTab(value: boolean): void {
    this.applyBoolean(this.quickFiltersAsMainTab, KEY_QUICK_FILTERS_AS_MAIN_TAB, value, { track: true });
  }

  toggleFilterSnapshotStorage(): void {
    this.toggleBoolean(this.filterSnapshotStorageEnabled, KEY_FILTER_SNAPSHOT_STORAGE);
  }

  setFilterSnapshotStorageEnabled(value: boolean): void {
    this.applyBoolean(this.filterSnapshotStorageEnabled, KEY_FILTER_SNAPSHOT_STORAGE, value, { track: true });
  }

  toggleFilterSnapshotUsername(): void {
    this.toggleBoolean(this.filterSnapshotUsernameEnabled, KEY_FILTER_SNAPSHOT_USERNAME);
  }

  setFilterSnapshotUsernameEnabled(value: boolean): void {
    this.applyBoolean(this.filterSnapshotUsernameEnabled, KEY_FILTER_SNAPSHOT_USERNAME, value, { track: true });
  }

  toggleFilterShareLinkUsername(): void {
    this.toggleBoolean(this.filterShareLinkUsernameEnabled, KEY_FILTER_SHARE_LINK_USERNAME);
  }

  setFilterShareLinkUsernameEnabled(value: boolean): void {
    this.applyBoolean(this.filterShareLinkUsernameEnabled, KEY_FILTER_SHARE_LINK_USERNAME, value, { track: true });
  }

  allowFilterSnapshotStorage(): void {
    this.setFilterSnapshotStorageEnabled(true);
    this.dismissFilterSnapshotConsentBannerPermanently();
  }

  optOutFilterSnapshotStorage(): void {
    this.setFilterSnapshotStorageEnabled(false);
    this.dismissFilterSnapshotConsentBannerPermanently();
  }

  dismissFilterSnapshotConsentBannerPermanently(): void {
    this.showFilterSnapshotConsentBanner.set(false);
    hideMessage(KEY_HIDE_FILTER_SNAPSHOT_CONSENT_BANNER);
  }

  toggleAutoPreviewPresetChanges(): void {
    this.toggleBoolean(this.autoPreviewPresetChanges, KEY_AUTO_PREVIEW_PRESET_CHANGES);
  }

  setAutoPreviewPresetChanges(value: boolean): void {
    this.applyBoolean(this.autoPreviewPresetChanges, KEY_AUTO_PREVIEW_PRESET_CHANGES, value, { track: true });
  }

  toggleHighlightStats(): void {
    this.toggleBoolean(this.highlightStats, KEY_HIGHLIGHT_STATS);
  }

  toggleIncludeCritInWeaponDps(): void {
    this.toggleBoolean(this.includeCritInWeaponDps, KEY_INCLUDE_CRIT_IN_WEAPON_DPS);
  }

  toggleKeepItemLabelTooltipsOpenOnHover(): void {
    this.toggleBoolean(this.keepItemLabelTooltipsOpenOnHover, KEY_KEEP_ITEM_LABEL_TOOLTIPS_OPEN_ON_HOVER);
  }

  toggleShowValueInDropdowns(): void {
    this.toggleBoolean(this.showValueInDropdowns, KEY_SHOW_VALUE_IN_DROPDOWNS);
  }

  setHideMovedCurrencyUniques(value: boolean): void {
    this.applyBoolean(this.hideMovedCurrencyUniques, KEY_HIDE_MOVED_CURRENCY_UNIQUES, value, {
      afterSet: (v) => this.showMovedTiers.set(!v),
    });
  }

  toggleHideMovedCurrencyUniques(): void {
    this.toggleBoolean(this.hideMovedCurrencyUniques, KEY_HIDE_MOVED_CURRENCY_UNIQUES, (v) => {
      this.showMovedTiers.set(!v);
    });
  }

  toggleHideIconsInDropdowns(): void {
    this.toggleBoolean(this.hideIconsInDropdowns, KEY_HIDE_ICONS_IN_DROPDOWNS);
  }

  toggleShowUniquesDustValuePerSlot(): void {
    this.toggleBoolean(this.showUniquesDustValuePerSlot, KEY_SHOW_UNIQUES_DUST_VALUE_PER_SLOT);
  }

  toggleStickyNavigation(): void {
    this.toggleBoolean(this.stickyNavigation, KEY_STICKY_NAVIGATION);
  }

  toggleFloatingControls(): void {
    this.toggleBoolean(this.floatingControls, KEY_FLOATING_CONTROLS);
  }

  toggleAddMoreColorPresets(): void {
    this.toggleBoolean(this.addMoreColorPresets, KEY_ADD_MORE_COLOR_PRESETS);
  }

  toggleEnableRuleNavigation(): void {
    this.toggleBoolean(this.enableRuleNavigation, KEY_ENABLE_RULE_NAVIGATION);
  }

  setEcoUpdateBehaviorLaunch(behavior: EcoUpdateBehavior): void {
    this.ecoUpdateBehaviorLaunch.set(behavior);
    localStorage.setItem(KEY_ECO_UPDATE_BEHAVIOR_LAUNCH, behavior);
    trackSetting(KEY_ECO_UPDATE_BEHAVIOR_LAUNCH, behavior);
  }

  toggleEcoUpdateBehaviorLaunchShowUpdates(): void {
    this.toggleBoolean(this.ecoUpdateBehaviorLaunchShowUpdates, KEY_ECO_UPDATE_BEHAVIOR_LAUNCH_SHOW_UPDATES);
  }

  setEcoUpdateBehaviorUpdate(behavior: EcoUpdateBehavior): void {
    this.ecoUpdateBehaviorUpdate.set(behavior);
    localStorage.setItem(KEY_ECO_UPDATE_BEHAVIOR_UPDATE, behavior);
    trackSetting(KEY_ECO_UPDATE_BEHAVIOR_UPDATE, behavior);
  }

  toggleEcoUpdateBehaviorUpdateEnable(): void {
    this.toggleBoolean(this.ecoUpdateBehaviorUpdateEnable, KEY_ECO_UPDATE_BEHAVIOR_UPDATE_ENABLE);
  }

  toggleEcoUpdateBehaviorUpdateBlink(): void {
    this.toggleBoolean(this.ecoUpdateBehaviorUpdateBlink, KEY_ECO_UPDATE_BEHAVIOR_UPDATE_BLINK);
  }

  toggleShowExtraTab(): void {
    this.toggleBoolean(this.showExtraTab, KEY_SHOW_EXTRA_TAB);
  }

  setEnableCustomRuleGroups(value: boolean): void {
    this.applyBoolean(this.enableCustomRuleGroups, KEY_ENABLE_CUSTOM_RULE_GROUPS, value);
  }

  toggleEnableCustomRuleGroups(): void {
    this.toggleBoolean(this.enableCustomRuleGroups, KEY_ENABLE_CUSTOM_RULE_GROUPS);
  }

  setEnableCustomRuleLabels(value: boolean): void {
    this.applyBoolean(this.enableCustomRuleLabels, KEY_ENABLE_CUSTOM_RULE_LABELS, value);
  }

  toggleEnableCustomRuleLabels(): void {
    this.toggleBoolean(this.enableCustomRuleLabels, KEY_ENABLE_CUSTOM_RULE_LABELS);
  }

  toggleQuickApplyRecentlyCopiedStylesInItemTierPicker(): void {
    this.toggleBoolean(
      this.quickApplyRecentlyCopiedStylesInItemTierPicker,
      KEY_QUICK_APPLY_STYLE_LIBRARY_IN_ITEM_TIER_PICKER
    );
  }

  toggleSkipRemoveConfirmDialogs(): void {
    this.toggleBoolean(this.skipRemoveConfirmDialogs, KEY_SKIP_REMOVE_CONFIRM_DIALOGS);
  }

  toggleAutocompleteRetainSearch(): void {
    this.toggleBoolean(this.autocompleteRetainSearch, KEY_AUTOCOMPLETE_RETAIN_SEARCH);
  }

  trackBackgroundChanged(previous: string): void {
    if (previous !== this.bgImage()) {
      trackSetting(KEY_BG_IMAGE, this.bgImage(), 'background');
    }
  }

  setBgImage(value: string): void {
    this.bgImage.set(value);
    localStorage.setItem(KEY_BG_IMAGE, value);
  }

  toggleShowDropInfo(): void {
    this.toggleBoolean(this.showDropInfo, KEY_SHOW_DROP_INFO);
  }

  toggleAlwaysGroupByBaseType(): void {
    this.setAlwaysGroupByBaseType(!this.alwaysGroupByBaseType());
  }

  toggleShowAllOtherUniques(): void {
    this.setShowAllOtherUniques(!this.showAllOtherUniques());
  }

  setAlwaysGroupByBaseType(value: boolean): void {
    this.alwaysGroupByBaseType.set(value);
    localStorage.setItem(KEY_ALWAYS_GROUP_BY_BASE_TYPE, value.toString());
  }

  setShowAllOtherUniques(value: boolean): void {
    this.showAllOtherUniques.set(value);
    localStorage.setItem(KEY_SHOW_ALL_OTHER_UNIQUES, value.toString());
  }

  setMovedTiersGroupingMode(mode: 'movement' | 'tier'): void {
    this.movedTiersGroupingMode.set(mode);
    localStorage.setItem(KEY_MOVED_TIERS_GROUPING_MODE, mode);
  }

  hideMovedTiersSection(): void {
    this.hideMovedCurrencyUniques.set(true);
    this.showMovedTiers.set(false);
    hideMessage(KEY_HIDE_MOVED_CURRENCY_UNIQUES);
  }

  dismissContinueWarning(): void {
    this.showContinueWarning.set(false);
    hideMessage(KEY_HIDE_CONTINUE_WARNING);
  }

  dismissTierListExplainer(): void {
    this.showTierListExplainer.set(false);
    hideMessage(KEY_HIDE_TIER_LISTS_MESSAGE);
  }

  dismissUniquesTierListMessage(): void {
    this.showUniquesTierListMessage.set(false);
    hideMessage(KEY_HIDE_UNIQUES_TIER_LIST_MESSAGE);
  }

  dismissModsTierListMessage(): void {
    this.showModsTierListMessage.set(false);
    hideMessage(KEY_HIDE_MODS_TIER_LIST_MESSAGE);
  }

  dismissGearTierListMessage(): void {
    this.showGearTierListMessage.set(false);
    hideMessage(KEY_HIDE_GEAR_TIER_LIST_MESSAGE);
  }

  dismissJewelleryTierListMessage(): void {
    this.showJewelleryTierListMessage.set(false);
    hideMessage(KEY_HIDE_JEWELLERY_TIER_LIST_MESSAGE);
  }

  dismissItemsTierListMessage(): void {
    this.showItemsTierListMessage.set(false);
    hideMessage(KEY_HIDE_ITEMS_TIER_LIST_MESSAGE);
  }

  dismissItemLibraryExplainer(): void {
    this.showItemLibraryExplainer.set(false);
    hideMessage(KEY_HIDE_ITEM_LIBRARY_EXPLAINER);
  }

  dismissPasteRuleExplainers(): void {
    this.showPasteRuleExplainers.set(false);
    hideMessage(KEY_HIDE_PASTE_RULE_EXPLAINERS);
  }

  dismissPasteUniqueExplainers(): void {
    this.showPasteUniqueExplainers.set(false);
    hideMessage(KEY_HIDE_PASTE_UNIQUE_EXPLAINERS);
  }

  dismissQuickFiltersMainTabBanner(): void {
    this.showQuickFiltersMainTabBanner.set(false);
    hideMessage(KEY_HIDE_QUICK_FILTERS_MAIN_TAB_BANNER);
  }

  dismissIncomingPatchWarning(): void {
    this.showIncomingPatchWarning.set(false);
    hideMessage(KEY_HIDE_UPCOMING_PATCH_WARNING);
  }

  dismissIncomingPatchTopBar(): void {
    this.showIncomingPatchTopBar.set(false);
    hideMessage(KEY_HIDE_UPCOMING_PATCH_TOP_BAR);
  }

  setLabelGlow(value: 'always' | 'hover' | 'never'): void {
    this.labelGlow.set(value);
    localStorage.setItem(KEY_LABEL_GLOW, value);
    trackSetting(KEY_LABEL_GLOW, value);
  }

  setLabelPreviewBg(value: LabelPreviewBackgroundId): void {
    this.labelPreviewBg.set(value);
    localStorage.setItem(KEY_LABEL_PREVIEW_BG, value);
    trackSetting(KEY_LABEL_PREVIEW_BG, value);
  }

  toggleNumericAlertSoundVolume(): void {
    this.toggleBoolean(this.numericAlertSoundVolume, KEY_NUMERIC_ALERT_SOUND_VOLUME);
  }

  toggleLimitGearTierListToMyGear(): void {
    this.toggleBoolean(this.limitGearTierListToMyGear, KEY_LIMIT_GEAR_TIER_LIST_TO_MY_GEAR);
  }

  setLimitGearTierListToMyGear(value: boolean): void {
    this.applyBoolean(this.limitGearTierListToMyGear, KEY_LIMIT_GEAR_TIER_LIST_TO_MY_GEAR, value);
  }

  toggleSettingsGroupVisibility(id: SettingsGroupId): void {
    const next = { ...this.settingsGroupVisibility(), [id]: !this.settingsGroupVisibility()[id] };
    this.settingsGroupVisibility.set(next);
    localStorage.setItem(KEY_SETTINGS_GROUP_VISIBILITY, JSON.stringify(next));
  }

  private readSettingsGroupVisibility(): SettingsGroupVisibility {
    const raw = localStorage.getItem(KEY_SETTINGS_GROUP_VISIBILITY);
    if (!raw) {
      return { ...DEFAULT_SETTINGS_GROUP_VISIBILITY };
    }
    try {
      const parsed = JSON.parse(raw) as Partial<Record<string, unknown>>;
      const result = { ...DEFAULT_SETTINGS_GROUP_VISIBILITY };
      for (const id of Object.keys(DEFAULT_SETTINGS_GROUP_VISIBILITY) as SettingsGroupId[]) {
        if (typeof parsed[id] === 'boolean') {
          result[id] = parsed[id];
        }
      }
      return result;
    } catch {
      return { ...DEFAULT_SETTINGS_GROUP_VISIBILITY };
    }
  }

  dismissTutorials(): void {
    this.applyBoolean(this.tutorialsDismissed, KEY_DISMISS_TUTORIALS, true);
  }

  setTutorialComplete(value: boolean): void {
    this.applyBoolean(this.tutorialComplete, KEY_TUTORIAL_COMPLETE, value);
  }

  resetTutorials(): void {
    this.tutorialsDismissed.set(false);
    this.tutorialComplete.set(false);
    clearMessage(KEY_DISMISS_TUTORIALS);
    clearMessage(KEY_TUTORIAL_COMPLETE);
  }

  resetAllWarningMessages(): void {
    clearMessage(KEY_HIDE_CONTINUE_WARNING);
    clearMessage(KEY_HIDE_GEAR_TIER_LIST_MESSAGE);
    clearMessage(KEY_HIDE_JEWELLERY_TIER_LIST_MESSAGE);
    clearMessage(KEY_HIDE_ITEMS_TIER_LIST_MESSAGE);
    clearMessage(KEY_HIDE_TIER_LISTS_MESSAGE);
    clearMessage(KEY_HIDE_UNIQUES_TIER_LIST_MESSAGE);
    clearMessage(KEY_HIDE_MODS_TIER_LIST_MESSAGE);
    clearMessage(KEY_HIDE_ITEM_LIBRARY_EXPLAINER);
    clearMessage(KEY_HIDE_QUICK_FILTERS_MAIN_TAB_BANNER);
    clearMessage(KEY_HIDE_FILTER_SNAPSHOT_CONSENT_BANNER);
    clearMessage(KEY_HIDE_UPCOMING_PATCH_WARNING);
    clearMessage(KEY_HIDE_UPCOMING_PATCH_TOP_BAR);
    clearMessage(KEY_HIDE_PASTE_RULE_EXPLAINERS);
    clearMessage(KEY_HIDE_PASTE_UNIQUE_EXPLAINERS);
    clearMessage(KEY_HIDE_PASTE_EXPLAINERS);

    this.showContinueWarning.set(true);
    this.showTierListExplainer.set(true);
    this.showUniquesTierListMessage.set(true);
    this.showModsTierListMessage.set(true);
    this.showGearTierListMessage.set(true);
    this.showJewelleryTierListMessage.set(true);
    this.showItemsTierListMessage.set(true);
    this.showItemLibraryExplainer.set(true);
    this.showQuickFiltersMainTabBanner.set(true);
    this.showFilterSnapshotConsentBanner.set(true);
    this.showIncomingPatchWarning.set(true);
    this.showIncomingPatchTopBar.set(true);
    this.showPasteRuleExplainers.set(true);
    this.showPasteUniqueExplainers.set(true);
  }
}
